import React, { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { Grid } from '@mui/material';
import { ONE_PAGE_SECTIONS } from 'constants/businessToolSections/onePage';
import { DATE_FORMAT_MM_DOT_DD_DOT_YYYY } from 'constants/formats';
import dayjs from 'dayjs';
import { withAuthenticatedStatus } from 'hocs/withAuthenticatedStatus';
import { useUpdateBusinessPlan } from 'services/businessPlan/hooks';
import { useBusinessToolStore } from 'store/useBusinessToolStore';
import { getBusinessPlan, getSetBusinessToolTitle } from 'store/useBusinessToolStore/selectors';
import { useUserStore } from 'store/useUserStore';
import { getAuthUser } from 'store/useUserStore/selectors';

import useCheckSubSectionAccess from 'hooks/useCheckSubSectionAccess';

import { CurrencyAutocomplete } from 'components/_basic/CurrencyAutocomplete';
import { DatePickerControl } from 'components/_basic/Form/DatePicker';
import { NumberInputControlled } from 'components/_basic/Form/NumberInput';
import { PhoneInputControlled } from 'components/_basic/Form/PhoneInput';
import { TextInputControl } from 'components/_basic/Form/TextInput';
import { FormHeader } from 'components/_basic/FormHeader';
import { FormSectionCard } from 'components/_basic/FormSectionCard';
import { FORM_SECTION_CARD_VARIANT } from 'components/_basic/FormSectionCard/types';
import { TooltipFormImageUpload } from 'components/BusinessToolForm/components/TooltipFormImageUpload';
import { useHandleRequiredForAIInputBlur } from 'components/BusinessToolForm/hooks/useHandleRequiredForAIInputBlur';
import {
  ABOUT_COMPANY_GENERAL_NAMES,
  prefix as aboutCompanyPrefix,
} from 'components/BusinessToolForm/Sections/AboutCompany/GeneralInfo/utils/form';
import { OperationIndustry } from 'components/BusinessToolForm/Sections/Global/components/OperationIndustry/OperationIndustry';
import { GLOBAL_FIELD_NAMES, globalPrefix } from 'components/BusinessToolForm/Sections/Global/form';
import {
  COMPANY_OVERVIEW_NAMES,
  prefix as companyOverviewPrefix,
} from 'components/BusinessToolForm/Sections/Overview/CompanyOverview/utils/form';
import { LifeCycleStages } from 'components/BusinessToolForm/Shared/LifeCycleStages';

import { getValidatedMetadata } from 'helpers/metadataParser';
import { concatWithDot } from 'helpers/string';

export const AboutTheCompany: React.FC = withAuthenticatedStatus(({ isAuth }) => {
  const { t } = useTranslation();
  const { control, getValues } = useFormContext();
  const [companyNamePrevState, setCompanyNamePrevState] = useState(
    getValues(concatWithDot(globalPrefix, GLOBAL_FIELD_NAMES.COMPANY_NAME)),
  );

  const { id: businessPlanId = '' } = useParams();

  const authUser = useUserStore(getAuthUser);

  const setBusinessToolTitle = useBusinessToolStore(getSetBusinessToolTitle);

  const businessPlan = useBusinessToolStore(getBusinessPlan);

  const updateMutation = useUpdateBusinessPlan();

  const canAccess = useCheckSubSectionAccess(ONE_PAGE_SECTIONS.ABOUT_THE_COMPANY);
  const handleRequiredForAIInputBlur = useHandleRequiredForAIInputBlur();

  const handleCompanyNameBlur = async () => {
    handleRequiredForAIInputBlur();
    const companyName = getValues(concatWithDot(globalPrefix, GLOBAL_FIELD_NAMES.COMPANY_NAME));
    const sameNames = companyNamePrevState === companyName;

    if (authUser && !sameNames) {
      try {
        const params = {
          id: businessPlanId,
          body: {
            title: companyName,
            metadata: JSON.stringify({
              ...getValidatedMetadata(businessPlan.metadata),
              isFirstTryToSetBusinessToolTitle: false,
            }),
          },
        };
        await updateMutation.mutateAsync(params);
        setBusinessToolTitle(companyName);
        setCompanyNamePrevState(companyName);
      } catch {
        return;
      }
    }
  };

  return (
    <>
      <FormSectionCard variant={FORM_SECTION_CARD_VARIANT.LARGE}>
        <FormHeader locked={!canAccess} title={t('aboutTheCompany')} section={ONE_PAGE_SECTIONS.ABOUT_THE_COMPANY} />
      </FormSectionCard>
      <FormSectionCard title={t('addCompanyLogoAndName')}>
        <Grid container spacing={{ xs: '16px', lg: '24px' }}>
          <Grid item xs="auto">
            <TooltipFormImageUpload
              width={64}
              height={64}
              placeholder="Logo"
              name={concatWithDot(companyOverviewPrefix, COMPANY_OVERVIEW_NAMES.FILE)}
              disabled={!canAccess}
              tooltipTitle={
                isAuth
                  ? 'Insert your company logo. Ensure the logo is high-resolution, appropriately sized and has transparent background.'
                  : 'Please login to upload pictures'
              }
              isTooltipHidden={false}
            />
          </Grid>
          {/* 0.1.1 */}
          <Grid item xs>
            <TextInputControl
              control={control}
              disabled={!canAccess}
              id="onePage.aboutTheCompany.name"
              name={concatWithDot(globalPrefix, GLOBAL_FIELD_NAMES.COMPANY_NAME)}
              label={t('overview.company.name')}
              maxLength={70}
              requiredForAI
              onBlur={handleCompanyNameBlur}
            />
          </Grid>
        </Grid>
      </FormSectionCard>
      <FormSectionCard title={t('overview.company.lifeCycleStage')}>
        <LifeCycleStages canAccess={!canAccess} />
      </FormSectionCard>
      <FormSectionCard>
        <Grid container spacing={3} display="flex" alignItems="flex-end">
          {/* 0.1.3 */}
          <Grid item xs={12} md={12} lg={6}>
            <DatePickerControl
              control={control}
              maxDate={dayjs().subtract(1, 'day')}
              disabled={!canAccess}
              id="onePage.aboutTheCompany.dateOfRegistration"
              name={concatWithDot(globalPrefix, GLOBAL_FIELD_NAMES.REGISTRATION_DATE)}
              label={t('overview.company.dateOfRegistration')}
              dateFormat={DATE_FORMAT_MM_DOT_DD_DOT_YYYY}
            />
          </Grid>
          {/* 0.1.5 */}
          <Grid item xs={12} md={12} lg={6}>
            <TextInputControl
              control={control}
              disabled={!canAccess}
              id="onePage.aboutTheCompany.address"
              name={concatWithDot(globalPrefix, GLOBAL_FIELD_NAMES.ADDRESS)}
              label={t('overview.company.address')}
              maxLength={150}
              requiredForAI
              onBlur={handleRequiredForAIInputBlur}
            />
          </Grid>
        </Grid>
      </FormSectionCard>
      {/* 0.1.4 */}
      <FormSectionCard title={t('overview.company.industry')} disabled={!canAccess} collapsible>
        <OperationIndustry
          sectorId="onePage.aboutTheCompany.sector"
          subSectorId="onePage.aboutTheCompany.subsector"
          industryGroupId="onePage.aboutTheCompany.industryGroup"
          industryId="onePage.aboutTheCompany.industry"
          disabled={!canAccess}
        />
      </FormSectionCard>
      <FormSectionCard
        title={t('overview.company.briefCompanyDescription')}
        aiFieldName={concatWithDot(globalPrefix, GLOBAL_FIELD_NAMES.COMPANY_BRIEF_DESCRIPTION)}
        aiOnlyUpdate
        disabled={!canAccess}
        requiredForAI
      >
        <TextInputControl
          multiline
          control={control}
          disabled={!canAccess}
          id="onePage.aboutTheCompany.briefCompanyDescription"
          name={concatWithDot(globalPrefix, GLOBAL_FIELD_NAMES.COMPANY_BRIEF_DESCRIPTION)}
          minRows={4}
          maxLength={1000}
          onBlur={handleRequiredForAIInputBlur}
        />
      </FormSectionCard>
      <FormSectionCard>
        <Grid container gap={3}>
          <Grid container spacing={3}>
            <Grid item lg={6} xs={12} md={12}>
              {/* 0.1.7 */}
              <NumberInputControlled
                control={control}
                disabled={!canAccess}
                id="onePage.aboutTheCompany.numberOfEmployers"
                name={concatWithDot(globalPrefix, GLOBAL_FIELD_NAMES.NUMBER_OF_EMPLOYEES)}
                label={t('overview.company.numberOfEmployers')}
              />
            </Grid>
          </Grid>
          <Grid container spacing={3} alignItems="flex-end">
            <Grid item lg={6} xs={12} md={12}>
              {/* 0.1.8 */}
              <CurrencyAutocomplete label={t('selectCurrencyForEntireDocument')} disabled={!canAccess} />
            </Grid>
            <Grid item lg={6} xs={12} md={12}>
              {/* 0.1.9 */}
              <NumberInputControlled
                control={control}
                disabled={!canAccess}
                adornmentType="currency"
                id="onePage.aboutTheCompany.averageAnnualIncome"
                name={concatWithDot(globalPrefix, GLOBAL_FIELD_NAMES.ANNUAL_INCOME)}
                label={t('overview.company.averageAnnualIncome')}
              />
            </Grid>
          </Grid>
        </Grid>
      </FormSectionCard>
      <FormSectionCard>
        <Grid container spacing={3} alignItems="flex-end">
          <Grid item lg={6} xs={12} md={12}>
            <TextInputControl
              control={control}
              disabled={!canAccess}
              id="onePage.aboutTheCompany.websiteUrl"
              name={concatWithDot(aboutCompanyPrefix, ABOUT_COMPANY_GENERAL_NAMES.WEBSITE_URL)}
              label={t('company.general.websiteUrl')}
              maxLength={150}
            />
          </Grid>
          <Grid item lg={6} xs={12} md={12}>
            <PhoneInputControlled
              control={control}
              disabled={!canAccess}
              id="onePage.aboutTheCompany.phone"
              name={concatWithDot(aboutCompanyPrefix, ABOUT_COMPANY_GENERAL_NAMES.PHONE)}
              subName={concatWithDot(aboutCompanyPrefix, ABOUT_COMPANY_GENERAL_NAMES.PHONE_FORMAT)}
              label={t('company.general.phone')}
            />
          </Grid>
          <Grid item lg={6} xs={12} md={12}>
            <TextInputControl
              control={control}
              disabled={!canAccess}
              id="onePage.aboutTheCompany.email"
              name={concatWithDot(aboutCompanyPrefix, ABOUT_COMPANY_GENERAL_NAMES.EMAIL)}
              label={t('company.general.email')}
              maxLength={150}
            />
          </Grid>
        </Grid>
      </FormSectionCard>
      <FormSectionCard title={t('company.general.contactPerson')}>
        <Grid container spacing={3}>
          <Grid item lg={6} xs={12} md={12}>
            <TextInputControl
              control={control}
              disabled={!canAccess}
              id="onePage.aboutTheCompany.firstName"
              name={concatWithDot(aboutCompanyPrefix, ABOUT_COMPANY_GENERAL_NAMES.FIRST_NAME)}
              label={t('company.general.firstName')}
              maxLength={150}
            />
          </Grid>
          <Grid item lg={6} xs={12} md={12}>
            <TextInputControl
              control={control}
              disabled={!canAccess}
              id="onePage.aboutTheCompany.lastName"
              name={concatWithDot(aboutCompanyPrefix, ABOUT_COMPANY_GENERAL_NAMES.LAST_NAME)}
              label={t('company.general.lastName')}
              maxLength={150}
            />
          </Grid>
          <Grid item lg={6} xs={12} md={12}>
            <TextInputControl
              control={control}
              disabled={!canAccess}
              id="onePage.aboutTheCompany.position"
              name={concatWithDot(aboutCompanyPrefix, ABOUT_COMPANY_GENERAL_NAMES.POSITION)}
              label={t('company.general.position')}
              maxLength={150}
            />
          </Grid>
        </Grid>
      </FormSectionCard>
    </>
  );
});
